import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';
import { activityTracker } from '../../services/activityTracker';
import { useTour } from '../../contexts/TourContext';
import { useDiamondSearchListener } from '../../hooks/useDiamondSearch';
import { notify } from '../../utils/notifications';
import { FiLock, FiSearch, FiFileText, FiHeart, FiSliders, FiZap, FiDownload, FiLayers, FiChevronLeft, FiChevronRight, FiLoader } from 'react-icons/fi';
import { PageContainer, Text, HighlightText, Button, } from '../../components/PageStyles';
import { ComponentGuard, useActionPermission } from '../../components/EnhancedPermissionGuards';
import { ComponentPermissions, ActionPermissions, RoleLevel } from '../../types/rbacImproved';
import { useAuthStore } from '../../stores/authStore';
import RequestMemoModal from '../../components/RequestMemoModal';
import { ShapeFilter, ColorFilter, ClarityFilter, CutQualityFilter, LabFilter } from '../../components/Diamond/Filters';
import { SAMPLE_DIAMONDS } from './utils/mockDiamonds';
import { ComparisonModal } from '../../components/Diamond/Comparison';
import { getDiamondShapeImage } from '../../utils/diamondShapeImages';
import { DiamondDetailModal } from '../../components/DiamondDetailModal';
import { PriceInquiryModal } from '../../components/PriceInquiryModal';
import { getCertificateUrl } from '../../utils/certificateUrls';
import { exportToCSV, exportToJSON, prepareDiamondsForExport } from '../../utils/exportDiamonds';
import { BaseModal } from '../../components/shared/BaseModal';
import styled from 'styled-components';
import { HeroSection, HeroTitle, HeroSubtitle, StatsGrid, StatCard, TabsContainer, TabsList, Tab, TabBadge, MainContent, FilterSection, FilterHeader, FilterTitle, SearchBar, SearchInput, AISearchButton, FilterGroup, FilterLabel, RangeGridFour, RangeInputGroup, RangeInput, FilterActions, ClearButton, ExportButton, ResultsHeader, ResultsCount, SortDropdown, GridContainer, DiamondsGridWrapper, DiamondsGrid, DiamondCardStyled, CardImage, WatchlistButton, LabBadge, CardContent, CardTitle, CardSpecs, SpecTag, CardDetails, CardPrice, CardActions, CardButton, CardButtonFull, PaginationSidebar, PageButton, MobileTopBar, MobileBottomBar, MobilePaginationButton, MobilePageInfo, FloatingCompareButton, CompareCount, AccessBanner, AccessDenied, EmptyState, SkeletonCard, SkeletonImage, SkeletonContent, SkeletonTitle, SkeletonSpecs, SkeletonSpec, SkeletonText, SkeletonPrice, SkeletonActions, SkeletonButton, LoadingHeader, } from './Atelier.styles';
// Filter layout styled components - Single column flow layout
const FilterMainGrid = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FilterPairRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;
const FilterFullWidth = styled.div `
  width: 100%;
`;
const FilterGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;
const Select = styled.select `
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #996515);
  }

  option {
    background: var(--bg-primary);
    color: var(--text-primary);
  }
`;
const FilterSectionTitle = styled.h4 `
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 0;
`;
// Request Access Banner Component (legacy - kept for specific use cases)
const RequestAccessBanner = styled.div `
  background: var(--accent-gold-bg, rgba(153, 101, 21, 0.08));
  border: 1px solid var(--accent-gold, #996515);
  padding: 20px;
  margin-bottom: 24px;
  text-align: center;
  color: var(--text-primary);

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 18px;
    margin-bottom: 12px;
    color: var(--accent-gold, #996515);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  p {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 16px;
    line-height: 1.6;
  }
`;
const FeatureCard = styled.div `
  position: relative;
`;
const Atelier = () => {
    const navigate = useNavigate();
    // Get authentication state from Zustand store
    const authStore = useAuthStore();
    const { isAuthenticated, user, hasPermission } = authStore;
    // Check if user can view the platform (guests can view but not use features)
    const canViewPlatform = hasPermission('page.platform.view') || isAuthenticated;
    const canAccessRealInventory = user && user.roleLevel >= RoleLevel.BASIC_DEALER;
    const canViewMockData = user && user.roleLevel >= RoleLevel.DEALER_APPLICANT;
    const canUseFeatures = canAccessRealInventory; // For backward compatibility
    // Initialize action permission hooks at component level (required by Rules of Hooks)
    const exportResultsPermission = useActionPermission(ActionPermissions.EXPORT_SEARCH_RESULTS);
    const viewDetailsPermission = useActionPermission(ActionPermissions.VIEW_DIAMOND_DETAILS);
    const requestMemoPermission = useActionPermission(ActionPermissions.REQUEST_DIAMOND_MEMO);
    // Tour context for tutorial support
    const { currentTour, currentStepIndex } = useTour();
    // State management
    const [activeTab, setActiveTab] = useState('inventory');
    const [diamonds, setDiamonds] = useState([]);
    const [filteredDiamonds, setFilteredDiamonds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 50;
    // Memo request modal state
    const [memoModalOpen, setMemoModalOpen] = useState(false);
    const [selectedDiamondForMemo, setSelectedDiamondForMemo] = useState(null);
    // Diamond detail modal state
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedDiamond, setSelectedDiamond] = useState(null);
    // Comparison state
    const [comparisonList, setComparisonList] = useState([]);
    const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
    // Price inquiry modal state
    const [priceInquiryModalOpen, setPriceInquiryModalOpen] = useState(false);
    const [priceInquiryDiamond, setPriceInquiryDiamond] = useState(null);
    // Export modal state
    const [exportModalOpen, setExportModalOpen] = useState(false);
    // Watchlist state (stored in localStorage)
    const [watchlist, setWatchlist] = useState(() => {
        const saved = localStorage.getItem('diamond-watchlist');
        return saved ? JSON.parse(saved) : [];
    });
    const toggleWatchlist = (diamondId) => {
        setWatchlist(prev => {
            const newList = prev.includes(diamondId)
                ? prev.filter(id => id !== diamondId)
                : [...prev, diamondId];
            localStorage.setItem('diamond-watchlist', JSON.stringify(newList));
            if (newList.includes(diamondId)) {
                notify.success('Added to Watchlist', 'Diamond saved to your watchlist');
            }
            else {
                notify.info('Removed', 'Diamond removed from watchlist');
            }
            return newList;
        });
    };
    const addToComparison = (diamond) => {
        if (comparisonList.length >= 4) {
            notify.warning('Comparison Limit', 'You can compare up to 4 diamonds at once');
            return;
        }
        if (comparisonList.find(d => d.id === diamond.id)) {
            notify.info('Already Added', 'This diamond is already in your comparison');
            return;
        }
        setComparisonList(prev => [...prev, diamond]);
        notify.success('Added to Comparison', `${diamond.shape} diamond added to comparison`);
    };
    const removeFromComparison = (diamondId) => {
        setComparisonList(prev => prev.filter(d => d.id !== diamondId));
    };
    // Export handlers
    const handleExportCSV = () => {
        const exportData = prepareDiamondsForExport(filteredDiamonds, false);
        const success = exportToCSV(exportData, 'royal-gems-diamonds');
        if (success) {
            notify.success('Export Complete', `Exported ${filteredDiamonds.length} diamonds to CSV`);
            setExportModalOpen(false);
        }
        else {
            notify.error('Export Failed', 'Failed to export diamonds to CSV');
        }
    };
    const handleExportJSON = () => {
        const exportData = prepareDiamondsForExport(filteredDiamonds, true);
        const success = exportToJSON(exportData, 'royal-gems-diamonds');
        if (success) {
            notify.success('Export Complete', `Exported ${filteredDiamonds.length} diamonds to JSON`);
            setExportModalOpen(false);
        }
        else {
            notify.error('Export Failed', 'Failed to export diamonds to JSON');
        }
    };
    // Smart filter states (matching DiamondInventory structure)
    const [filters, setFilters] = useState({
        shape: '',
        color: '',
        clarity: '',
        cut: '',
        lab: '',
        minCarat: '',
        maxCarat: '',
        minPrice: '',
        maxPrice: '',
        status: '',
        search: '',
        certification: '' // Keep for backward compatibility
    });
    // Use imported sample diamonds for mock data
    const sampleDiamonds = SAMPLE_DIAMONDS;
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };
    const clearFilters = () => {
        setFilters({
            shape: '',
            color: '',
            clarity: '',
            cut: '',
            lab: '',
            minCarat: '',
            maxCarat: '',
            minPrice: '',
            maxPrice: '',
            status: '',
            search: '',
            certification: ''
        });
    };
    // Helper: Find best matching value from available options (case-insensitive, partial match)
    const findBestMatch = (value, availableOptions) => {
        if (!value)
            return '';
        const lowerValue = value.toLowerCase();
        // Exact match (case-insensitive)
        const exactMatch = availableOptions.find(opt => opt.toLowerCase() === lowerValue);
        if (exactMatch)
            return exactMatch;
        // Partial match (value contains or is contained by option)
        const partialMatch = availableOptions.find(opt => opt.toLowerCase().includes(lowerValue) || lowerValue.includes(opt.toLowerCase()));
        if (partialMatch)
            return partialMatch;
        // No match found
        return '';
    };
    // Listen for AI chat search events
    const handleAISearch = useCallback((searchFilters) => {
        console.log('🤖 [Atelier] AI Search event received:', searchFilters);
        // Get current data source
        const currentDataSource = canAccessRealInventory ? diamonds : sampleDiamonds;
        let filteredData = [...currentDataSource];
        // Track what filters we'll actually apply and what was skipped
        const appliedFilters = {};
        const skippedFilters = [];
        // Get all available options from full dataset
        const allShapes = [...new Set(currentDataSource.map(d => d.shape))].filter(Boolean);
        const allColors = [...new Set(currentDataSource.map(d => d.color))].filter(Boolean);
        const allClarities = [...new Set(currentDataSource.map(d => d.clarity))].filter(Boolean);
        const allCuts = [...new Set(currentDataSource.map(d => d.cut))].filter(Boolean);
        const allLabs = [...new Set(currentDataSource.map(d => d.lab || d.certification))].filter((lab) => Boolean(lab));
        console.log('🤖 [Atelier] All available options:', { shapes: allShapes, colors: allColors, clarities: allClarities });
        // Step 1: Match and apply SHAPE filter first (most restrictive usually)
        if (searchFilters.shape) {
            const matchedShape = findBestMatch(searchFilters.shape, allShapes);
            if (matchedShape) {
                filteredData = filteredData.filter(d => d.shape.toLowerCase() === matchedShape.toLowerCase());
                appliedFilters.shape = matchedShape;
                console.log(`🤖 Step 1 - Shape "${searchFilters.shape}" → "${matchedShape}": ${filteredData.length} diamonds`);
            }
            else {
                skippedFilters.push(`shape "${searchFilters.shape}"`);
                console.log(`🤖 Step 1 - Shape "${searchFilters.shape}" not found in inventory`);
            }
        }
        // Step 2: Apply PRICE filter early (reduces set significantly)
        if (searchFilters.maxPrice) {
            const maxPrice = parseFloat(searchFilters.maxPrice);
            const beforeCount = filteredData.length;
            filteredData = filteredData.filter(d => {
                const price = d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, ''));
                return price <= maxPrice;
            });
            appliedFilters.maxPrice = searchFilters.maxPrice;
            console.log(`🤖 Step 2 - Max price $${maxPrice}: ${beforeCount} → ${filteredData.length} diamonds`);
        }
        if (searchFilters.minPrice) {
            const minPrice = parseFloat(searchFilters.minPrice);
            const beforeCount = filteredData.length;
            filteredData = filteredData.filter(d => {
                const price = d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, ''));
                return price >= minPrice;
            });
            appliedFilters.minPrice = searchFilters.minPrice;
            console.log(`🤖 Step 2 - Min price $${minPrice}: ${beforeCount} → ${filteredData.length} diamonds`);
        }
        // Step 3: Check what COLOR options are available AFTER shape+price filtering
        if (searchFilters.color) {
            const availableColors = [...new Set(filteredData.map(d => d.color))].filter(Boolean);
            const matchedColor = findBestMatch(searchFilters.color, availableColors);
            if (matchedColor) {
                filteredData = filteredData.filter(d => d.color === matchedColor);
                appliedFilters.color = matchedColor;
                console.log(`🤖 Step 3 - Color "${searchFilters.color}" → "${matchedColor}": ${filteredData.length} diamonds (available: ${availableColors.join(', ')})`);
            }
            else {
                skippedFilters.push(`color "${searchFilters.color}" (available: ${availableColors.slice(0, 5).join(', ')})`);
                console.log(`🤖 Step 3 - Color "${searchFilters.color}" not available. Options: ${availableColors.join(', ')}`);
            }
        }
        // Step 4: Check what CLARITY options are available AFTER previous filters
        if (searchFilters.clarity) {
            const availableClarities = [...new Set(filteredData.map(d => d.clarity))].filter(Boolean);
            const matchedClarity = findBestMatch(searchFilters.clarity, availableClarities);
            if (matchedClarity) {
                filteredData = filteredData.filter(d => d.clarity === matchedClarity);
                appliedFilters.clarity = matchedClarity;
                console.log(`🤖 Step 4 - Clarity "${searchFilters.clarity}" → "${matchedClarity}": ${filteredData.length} diamonds`);
            }
            else {
                skippedFilters.push(`clarity "${searchFilters.clarity}" (available: ${availableClarities.slice(0, 5).join(', ')})`);
                console.log(`🤖 Step 4 - Clarity "${searchFilters.clarity}" not available. Options: ${availableClarities.join(', ')}`);
            }
        }
        // Step 5: Check what CUT options are available
        if (searchFilters.cut) {
            const availableCuts = [...new Set(filteredData.map(d => d.cut))].filter(Boolean);
            const matchedCut = findBestMatch(searchFilters.cut, availableCuts);
            if (matchedCut) {
                filteredData = filteredData.filter(d => d.cut === matchedCut);
                appliedFilters.cut = matchedCut;
                console.log(`🤖 Step 5 - Cut "${searchFilters.cut}" → "${matchedCut}": ${filteredData.length} diamonds`);
            }
            else {
                skippedFilters.push(`cut "${searchFilters.cut}"`);
            }
        }
        // Step 6: Check what LAB options are available
        if (searchFilters.lab) {
            const availableLabs = [...new Set(filteredData.map(d => d.lab || d.certification))].filter((lab) => Boolean(lab));
            const matchedLab = findBestMatch(searchFilters.lab, availableLabs);
            if (matchedLab) {
                filteredData = filteredData.filter(d => (d.lab || d.certification) === matchedLab);
                appliedFilters.lab = matchedLab;
                console.log(`🤖 Step 6 - Lab "${searchFilters.lab}" → "${matchedLab}": ${filteredData.length} diamonds`);
            }
            else {
                skippedFilters.push(`lab "${searchFilters.lab}"`);
            }
        }
        // Step 7: Apply carat filters
        if (searchFilters.minCarat) {
            appliedFilters.minCarat = searchFilters.minCarat;
        }
        if (searchFilters.maxCarat) {
            appliedFilters.maxCarat = searchFilters.maxCarat;
        }
        console.log('🤖 [Atelier] Final result:', {
            appliedFilters,
            skippedFilters,
            resultCount: filteredData.length
        });
        // Build the filter state
        const newFilters = {
            shape: appliedFilters.shape || '',
            color: appliedFilters.color || '',
            clarity: appliedFilters.clarity || '',
            cut: appliedFilters.cut || '',
            lab: appliedFilters.lab || '',
            minCarat: appliedFilters.minCarat || '',
            maxCarat: appliedFilters.maxCarat || '',
            minPrice: appliedFilters.minPrice || '',
            maxPrice: appliedFilters.maxPrice || '',
            search: '',
            status: '',
            certification: ''
        };
        setFilters(newFilters);
        // Show notification
        if (skippedFilters.length > 0) {
            notify.warning('AI Search Applied (adjusted)', `Skipped unavailable: ${skippedFilters.join(', ')}`);
        }
        else {
            notify.success('AI Search Applied', `Found ${filteredData.length} matching diamonds`);
        }
        // Reset to page 1
        setCurrentPage(1);
    }, [canAccessRealInventory, diamonds, sampleDiamonds]);
    // Register the search listener
    useDiamondSearchListener(handleAISearch);
    // Get unique values for filter dropdowns (use appropriate data source)
    const dataSource = canAccessRealInventory ? diamonds : sampleDiamonds;
    const uniqueShapes = [...new Set(dataSource.map(d => d.shape))].filter(Boolean).sort();
    const uniqueColors = [...new Set(dataSource.map(d => d.color))].filter(Boolean).sort();
    const uniqueClarities = [...new Set(dataSource.map(d => d.clarity))].filter(Boolean).sort();
    const uniqueCuts = [...new Set(dataSource.map(d => d.cut))].filter(Boolean).sort();
    const uniqueLabs = [...new Set(dataSource.map(d => d.lab || d.certification).filter((lab) => Boolean(lab)))].sort();
    // Smart filtering: Get available diamonds based on current filters (excluding the filter being checked)
    const getAvailableDiamondsForFilter = (excludeFilter) => {
        let available = dataSource;
        // Apply all filters except the one we're checking
        if (excludeFilter !== 'shape' && filters.shape) {
            available = available.filter(d => d.shape.toLowerCase() === filters.shape.toLowerCase());
        }
        if (excludeFilter !== 'color' && filters.color) {
            available = available.filter(d => d.color === filters.color);
        }
        if (excludeFilter !== 'clarity' && filters.clarity) {
            available = available.filter(d => d.clarity === filters.clarity);
        }
        if (excludeFilter !== 'cut' && filters.cut) {
            available = available.filter(d => d.cut === filters.cut);
        }
        if (excludeFilter !== 'lab' && filters.lab) {
            available = available.filter(d => (d.lab || d.certification) === filters.lab);
        }
        // Apply search filter
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            available = available.filter(d => d.shape.toLowerCase().includes(searchTerm) ||
                d.color.toLowerCase().includes(searchTerm) ||
                d.clarity.toLowerCase().includes(searchTerm) ||
                (d.lab || d.certification || '').toLowerCase().includes(searchTerm) ||
                d.id.toLowerCase().includes(searchTerm));
        }
        // Apply carat range
        if (filters.minCarat) {
            const minCarat = parseFloat(filters.minCarat);
            available = available.filter(d => (d.caratWeight || parseFloat(d.carat || '0')) >= minCarat);
        }
        if (filters.maxCarat) {
            const maxCarat = parseFloat(filters.maxCarat);
            available = available.filter(d => (d.caratWeight || parseFloat(d.carat || '0')) <= maxCarat);
        }
        // Apply price range
        if (filters.minPrice) {
            const minPrice = parseFloat(filters.minPrice);
            available = available.filter(d => {
                const price = d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, ''));
                return price >= minPrice;
            });
        }
        if (filters.maxPrice) {
            const maxPrice = parseFloat(filters.maxPrice);
            available = available.filter(d => {
                const price = d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, ''));
                return price <= maxPrice;
            });
        }
        return available;
    };
    // Smart count helpers - only count diamonds that would be available with current filters
    const getShapeCount = (shape) => {
        const available = getAvailableDiamondsForFilter('shape');
        return available.filter(d => d.shape.toLowerCase() === shape.toLowerCase()).length;
    };
    const getColorCount = (color) => {
        const available = getAvailableDiamondsForFilter('color');
        return available.filter(d => d.color === color).length;
    };
    const getClarityCount = (clarity) => {
        const available = getAvailableDiamondsForFilter('clarity');
        return available.filter(d => d.clarity === clarity).length;
    };
    const getCutCount = (cut) => {
        const available = getAvailableDiamondsForFilter('cut');
        return available.filter(d => d.cut === cut).length;
    };
    const getLabCount = (lab) => {
        const available = getAvailableDiamondsForFilter('lab');
        return available.filter(d => (d.lab || d.certification) === lab).length;
    };
    // Available shape images (must match files in /public/diamondshape/)
    const availableShapeImages = [
        'round', 'oval', 'princess', 'emerald', 'asscher', 'cushion',
        'marquise', 'radiant', 'pear', 'heart'
    ];
    // Smart Filter Component Functions
    // ShapeFilterComponent removed - now using shared ShapeFilter component
    // ColorFilterComponent removed - now using shared ColorFilter component
    // Clarity quality mapping
    const clarityQuality = {
        'FL': 10, 'IF': 9, 'VVS1': 8, 'VVS2': 7, 'VS1': 6, 'VS2': 5, 'SI1': 4, 'SI2': 3, 'I1': 2, 'I2': 1, 'I3': 0
    };
    // Cut quality mapping
    const cutQuality = {
        'Excellent': 5, 'Very Good': 4, 'Good': 3, 'Fair': 2, 'Poor': 1
    };
    // Available lab seal images (must match files in /public/seal/)
    const availableLabSeals = [
        'gia', 'ags', 'egi', 'hrd', 'igi'
    ];
    // Load diamonds from API
    const loadInventory = async () => {
        try {
            setIsLoading(true);
            setError('');
            // Build query parameters for API
            const queryParams = new URLSearchParams();
            queryParams.append('limit', '1000'); // Changed from 50 to 1000
            queryParams.append('sortBy', 'priceUSD');
            queryParams.append('sortOrder', 'asc');
            // Add current filters to API query
            if (filters.shape)
                queryParams.append('shape', filters.shape.toLowerCase());
            if (filters.color)
                queryParams.append('color', filters.color);
            if (filters.clarity)
                queryParams.append('clarity', filters.clarity);
            // Build endpoint with query params
            const endpoint = `diamonds-inventory?${queryParams}`;
            console.log('💎 Atelier - Loading diamond inventory from API...');
            // Use apiClient for automatic token refresh on 401
            const response = await apiClient.get(endpoint);
            if (!response.ok || !response.data?.success) {
                throw new Error(response.error || response.data?.message || `API Error: ${response.status}`);
            }
            const data = response.data;
            console.log(`💎 Atelier - Loaded ${data.data.diamonds.length} diamonds from API`);
            setDiamonds(data.data.diamonds);
            setFilteredDiamonds(data.data.diamonds);
        }
        catch (error) {
            console.error('❌ Atelier - Error loading diamonds:', error);
            setError(`Failed to load diamond inventory: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setDiamonds([]);
            setFilteredDiamonds([]);
        }
        finally {
            setIsLoading(false);
        }
    };
    // Normalize diamond data for consistent display
    const normalizeDiamond = (diamond) => ({
        ...diamond,
        carat: diamond.caratWeight ? diamond.caratWeight.toString() : diamond.carat,
        price: diamond.priceDisplay || ((diamond.priceUSD || diamond.totalPrice) ? `$${(diamond.priceUSD || diamond.totalPrice).toLocaleString()}` : 'Price available on inquiry'),
        certification: diamond.lab || diamond.certification
    });
    // Auto-open diamond detail modal when memo request tutorial needs it
    useEffect(() => {
        const handleOpenDetailModal = () => {
            console.log('📬 Received openDiamondDetailModalForTour event');
            // Scroll to top to fix Joyride spotlight positioning
            // Try multiple scroll targets since modal might block window scroll
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (filteredDiamonds.length > 0) {
                const firstDiamond = normalizeDiamond(filteredDiamonds[0]);
                console.log('🎯 Opening detail modal with first diamond:', firstDiamond.id);
                setSelectedDiamond(firstDiamond);
                setDetailModalOpen(true);
            }
            else {
                console.warn('⚠️ No diamonds available to open detail modal with');
            }
        };
        window.addEventListener('openDiamondDetailModalForTour', handleOpenDetailModal);
        return () => {
            window.removeEventListener('openDiamondDetailModalForTour', handleOpenDetailModal);
        };
    }, [filteredDiamonds]);
    // Auto-open memo modal when memo request tutorial needs it
    useEffect(() => {
        const handleOpenMemoModal = () => {
            console.log('📬 Received openMemoModalForTour event');
            // Scroll to top to fix Joyride spotlight positioning
            window.scrollTo({ top: 0, behavior: 'instant' });
            // Use the currently selected diamond from DiamondDetailModal, or fall back to first diamond
            const diamondToUse = selectedDiamond || (filteredDiamonds.length > 0 ? normalizeDiamond(filteredDiamonds[0]) : null);
            if (diamondToUse) {
                console.log('🎯 Opening memo modal with diamond:', diamondToUse.id);
                // Close the diamond detail modal first
                setDetailModalOpen(false);
                // Then open the memo modal
                setSelectedDiamondForMemo(diamondToUse);
                setMemoModalOpen(true);
            }
            else {
                console.warn('⚠️ No diamonds available to open modal with');
            }
        };
        // Listen for custom event from tour context
        window.addEventListener('openMemoModalForTour', handleOpenMemoModal);
        return () => {
            window.removeEventListener('openMemoModalForTour', handleOpenMemoModal);
        };
    }, [filteredDiamonds, selectedDiamond]);
    // Apply filters to diamonds
    useEffect(() => {
        const sourceData = canAccessRealInventory ? diamonds : sampleDiamonds;
        let filtered = sourceData;
        // Debug: Show what's in the data
        const allShapes = [...new Set(sourceData.map(d => d.shape))].sort();
        const allClarities = [...new Set(sourceData.map(d => d.clarity))].sort();
        console.log('📊 [Atelier] Applying filters:', {
            activeFilters: filters,
            sourceDataCount: sourceData.length,
            usingRealInventory: canAccessRealInventory,
            availableShapes: allShapes,
            availableClarities: allClarities,
            sampleRecord: sourceData[0] ? {
                shape: sourceData[0].shape,
                clarity: sourceData[0].clarity,
                color: sourceData[0].color,
                price: sourceData[0].priceUSD
            } : 'No data'
        });
        // Shape filter
        if (filters.shape) {
            const beforeCount = filtered.length;
            const availableShapes = [...new Set(filtered.map(d => d.shape))];
            filtered = filtered.filter(d => d.shape.toLowerCase() === filters.shape.toLowerCase());
            console.log(`  → Shape "${filters.shape}": ${beforeCount} → ${filtered.length} diamonds`);
            if (filtered.length === 0 && beforeCount > 0) {
                console.warn(`  ⚠️ No diamonds match shape "${filters.shape}"`);
                console.warn(`    Available shapes in data: [${availableShapes.slice(0, 10).join(', ')}]`);
            }
        }
        // Color filter
        if (filters.color) {
            const beforeCount = filtered.length;
            filtered = filtered.filter(d => d.color === filters.color);
            console.log(`  → Color "${filters.color}": ${beforeCount} → ${filtered.length} diamonds`);
        }
        // Clarity filter
        if (filters.clarity) {
            const beforeCount = filtered.length;
            const availableClarities = [...new Set(filtered.map(d => d.clarity))];
            filtered = filtered.filter(d => d.clarity === filters.clarity);
            console.log(`  → Clarity "${filters.clarity}": ${beforeCount} → ${filtered.length} diamonds`);
            console.log(`    Available clarities in current set: [${availableClarities.join(', ')}]`);
            if (filtered.length === 0 && beforeCount > 0) {
                console.warn(`  ⚠️ No diamonds match clarity "${filters.clarity}" - available: [${availableClarities.join(', ')}]`);
            }
        }
        // Cut filter
        if (filters.cut) {
            filtered = filtered.filter(d => d.cut === filters.cut);
        }
        // Lab filter
        if (filters.lab || filters.certification) {
            const lab = filters.lab || filters.certification;
            filtered = filtered.filter(d => (d.lab || d.certification) === lab);
        }
        // Carat range
        if (filters.minCarat) {
            const minCarat = parseFloat(filters.minCarat);
            filtered = filtered.filter(d => (d.caratWeight || parseFloat(d.carat || '0')) >= minCarat);
        }
        if (filters.maxCarat) {
            const maxCarat = parseFloat(filters.maxCarat);
            filtered = filtered.filter(d => (d.caratWeight || parseFloat(d.carat || '0')) <= maxCarat);
        }
        // Price range
        if (filters.minPrice) {
            const beforeCount = filtered.length;
            const minPrice = parseFloat(filters.minPrice);
            filtered = filtered.filter(d => {
                const price = d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, ''));
                return price >= minPrice;
            });
            console.log(`  → Min price $${minPrice}: ${beforeCount} → ${filtered.length} diamonds`);
        }
        if (filters.maxPrice) {
            const beforeCount = filtered.length;
            const maxPrice = parseFloat(filters.maxPrice);
            const pricesInRange = filtered.map(d => d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, '')));
            filtered = filtered.filter(d => {
                const price = d.priceUSD || parseFloat((d.price || '$0').replace(/[$,]/g, ''));
                return price <= maxPrice;
            });
            console.log(`  → Max price $${maxPrice}: ${beforeCount} → ${filtered.length} diamonds`);
            if (filtered.length === 0 && beforeCount > 0) {
                const minAvailablePrice = Math.min(...pricesInRange);
                console.warn(`  ⚠️ No diamonds under $${maxPrice} - cheapest available: $${minAvailablePrice}`);
            }
        }
        // Search filter
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(d => d.shape.toLowerCase().includes(searchTerm) ||
                d.color.toLowerCase().includes(searchTerm) ||
                d.clarity.toLowerCase().includes(searchTerm) ||
                (d.lab || d.certification || '').toLowerCase().includes(searchTerm) ||
                d.id.toLowerCase().includes(searchTerm));
        }
        console.log('📊 [Atelier] Final filter result:', {
            totalFiltered: filtered.length,
            sampleDiamonds: filtered.slice(0, 3).map(d => ({
                id: d.id,
                shape: d.shape,
                clarity: d.clarity,
                price: d.priceUSD || d.price
            }))
        });
        setFilteredDiamonds(filtered);
        setCurrentPage(1); // Reset to page 1 when filters change
    }, [diamonds, filters, canAccessRealInventory, sampleDiamonds]);
    // Pagination calculation
    const paginatedDiamonds = filteredDiamonds.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const totalPages = Math.ceil(filteredDiamonds.length / ITEMS_PER_PAGE);
    // Load diamonds on component mount and when filters change
    useEffect(() => {
        if (canAccessRealInventory) {
            // Only Level 4+ (BASIC_DEALER) gets real inventory
            loadInventory();
        }
        else {
            // Everyone else gets mock/sample data
            setIsLoading(false);
            setDiamonds([]);
            setFilteredDiamonds([]);
        }
    }, [canAccessRealInventory]);
    if (!canViewPlatform) {
        return (_jsx(PageContainer, { children: _jsxs(AccessDenied, { children: [_jsx("h2", { children: "Royal Gems Platform Access Required" }), _jsx("p", { children: "The Royal Gems Platform is exclusively available to registered and verified dealers. Access our complete inventory of 0.40 to 20.00+ carat diamonds with real-time availability and competitive wholesale pricing." }), _jsxs("p", { children: ["To request dealer access, please contact us at", ' ', _jsx(HighlightText, { children: "info@royalgemsusa.com" }), " or call", ' ', _jsx(HighlightText, { children: "1-513-381-0339" })] }), _jsxs("div", { style: { marginTop: '32px' }, children: [_jsx(Button, { onClick: () => navigate('/login'), children: "Login to Platform" }), _jsx(Button, { variant: "secondary", style: { marginLeft: '16px' }, onClick: () => navigate('/contact'), children: "Request Access" })] })] }) }));
    }
    return (_jsxs(PageContainer, { children: [_jsxs(HeroSection, { children: [_jsxs(HeroTitle, { children: ["Royal Gems ", _jsx("span", { children: "Platform" })] }), _jsx(HeroSubtitle, { children: "Browse our curated collection of certified diamonds with real-time availability" })] }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: canAccessRealInventory ? diamonds.length.toLocaleString() : '15,000+' }), _jsx("div", { className: "stat-label", children: canAccessRealInventory ? 'Live Inventory' : 'Platform Collection' })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: "0.40-20+" }), _jsx("div", { className: "stat-label", children: "Carat Range" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: "24/7" }), _jsx("div", { className: "stat-label", children: "Priority Shipping" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: uniqueLabs.length }), _jsx("div", { className: "stat-label", children: "Certifications" })] })] }), _jsx(TabsContainer, { children: _jsxs(TabsList, { children: [_jsxs(Tab, { "$active": activeTab === 'inventory', onClick: () => setActiveTab('inventory'), children: [_jsx(FiSearch, {}), " Inventory Search"] }), _jsxs(Tab, { "$active": activeTab === 'watchlist', onClick: () => setActiveTab('watchlist'), children: [_jsx(FiHeart, {}), " Watchlist", watchlist.length > 0 && _jsx(TabBadge, { children: watchlist.length })] })] }) }), _jsxs(MainContent, { children: [canViewMockData && !canAccessRealInventory && (_jsxs(AccessBanner, { "$variant": "success", children: [_jsxs("h3", { children: [_jsx(FiFileText, { size: 24 }), "Viewing Mock Inventory"] }), _jsx("p", { children: "You're viewing sample diamond data to explore the platform features. To access our live inventory of certified diamonds, please request access." }), _jsxs("div", { style: { display: 'flex', gap: '16px', justifyContent: 'center' }, children: [_jsx(Button, { onClick: () => navigate('/contact'), children: "Request Live Inventory Access" }), _jsx(Button, { variant: "secondary", onClick: () => navigate('/'), children: "Learn More About Royal Gems" })] })] })), !canViewMockData && isAuthenticated && (_jsxs(AccessBanner, { "$variant": "info", children: [_jsxs("h3", { children: [_jsx(FiLock, { size: 24 }), "Platform Access Required"] }), _jsx("p", { children: "Welcome to the Royal Gems Platform! To browse our exclusive diamond collections and access detailed inventory information, please request platform access." }), _jsxs("div", { style: { display: 'flex', gap: '16px', justifyContent: 'center' }, children: [_jsx(Button, { onClick: () => navigate('/contact'), children: "Request Platform Access" }), _jsx(Button, { variant: "secondary", onClick: () => navigate('/'), children: "Learn More About Royal Gems" })] })] })), activeTab === 'inventory' && (_jsxs(_Fragment, { children: [!isLoading && (_jsx(FeatureCard, { "$hasAccess": !!canUseFeatures, children: _jsxs(FilterSection, { id: "tour-filter-section", children: [_jsx(FilterHeader, { children: _jsxs(FilterTitle, { children: [_jsx(FiSliders, {}), " Advanced Diamond Search"] }) }), !canUseFeatures && (_jsxs(RequestAccessBanner, { children: [_jsxs("h3", { children: [_jsx(FiSearch, { size: 20 }), "Advanced Search Access Required"] }), _jsx("p", { children: "This advanced search functionality is available to approved users. Apply for access to unlock all search features and filters." }), _jsx(Button, { onClick: () => navigate('/contact'), children: "Request Access" })] })), _jsxs(SearchBar, { children: [_jsx(SearchInput, { id: "search", type: "text", placeholder: "Search by ID, shape, color, clarity, or certificate number...", value: filters.search, onChange: (e) => handleFilterChange('search', e.target.value) }), _jsx(ComponentGuard, { componentPermission: ComponentPermissions.USE_AI_POWERED_SEARCH, fallback: null, children: _jsxs(AISearchButton, { children: [_jsx(FiZap, {}), " AI Search"] }) })] }), _jsxs(FilterMainGrid, { children: [_jsx(FilterFullWidth, { children: _jsx(ShapeFilter, { selectedShape: filters.shape, shapes: uniqueShapes, onShapeChange: (shape) => handleFilterChange('shape', shape), getShapeCount: getShapeCount }) }), _jsxs(FilterPairRow, { children: [_jsx(ColorFilter, { selectedColor: filters.color, colors: uniqueColors, onColorChange: (color) => handleFilterChange('color', color), getColorCount: getColorCount }), _jsx(CutQualityFilter, { selectedCut: filters.cut, cuts: uniqueCuts, onCutChange: (cut) => handleFilterChange('cut', cut), getCutCount: getCutCount, cutQuality: cutQuality })] }), _jsxs(FilterPairRow, { children: [_jsx(ClarityFilter, { selectedClarity: filters.clarity, clarities: uniqueClarities, onClarityChange: (clarity) => handleFilterChange('clarity', clarity), getClarityCount: getClarityCount, clarityQuality: clarityQuality }), _jsx(LabFilter, { selectedLab: filters.lab, labs: uniqueLabs, onLabChange: (lab) => handleFilterChange('lab', lab), getLabCount: getLabCount })] }), _jsx(FilterFullWidth, { children: _jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "Size & Price Ranges" }), _jsxs(RangeGridFour, { children: [_jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "minCarat", children: "Min Carat" }), _jsx(RangeInput, { id: "minCarat", type: "number", step: "0.01", placeholder: "0.00", value: filters.minCarat, onChange: (e) => handleFilterChange('minCarat', e.target.value) })] }), _jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "maxCarat", children: "Max Carat" }), _jsx(RangeInput, { id: "maxCarat", type: "number", step: "0.01", placeholder: "10.00", value: filters.maxCarat, onChange: (e) => handleFilterChange('maxCarat', e.target.value) })] }), _jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "minPrice", children: "Min Price ($)" }), _jsx(RangeInput, { id: "minPrice", type: "number", placeholder: "0", value: filters.minPrice, onChange: (e) => handleFilterChange('minPrice', e.target.value) })] }), _jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "maxPrice", children: "Max Price ($)" }), _jsx(RangeInput, { id: "maxPrice", type: "number", placeholder: "1000000", value: filters.maxPrice, onChange: (e) => handleFilterChange('maxPrice', e.target.value) })] })] })] }) })] }), _jsxs(FilterActions, { children: [_jsx(ClearButton, { onClick: clearFilters, children: "Clear All Filters" }), _jsx("div", { style: { display: 'flex', gap: '12px' }, children: _jsx(ComponentGuard, { componentPermission: ComponentPermissions.EXPORT_SEARCH_RESULTS, fallback: _jsxs(ExportButton, { disabled: true, title: "Export requires Senior Dealer status", children: [_jsx(FiDownload, {}), " Export"] }), showPlaceholder: true, children: _jsxs(ExportButton, { id: "tour-export-button", onClick: () => {
                                                                exportResultsPermission.performAction(() => setExportModalOpen(true));
                                                            }, children: [_jsx(FiDownload, {}), " Export Results"] }) }) })] })] }) })), _jsxs(FeatureCard, { "$hasAccess": !!canAccessRealInventory, children: [!isLoading && (_jsxs(ResultsHeader, { children: [_jsxs(ResultsCount, { children: ["Showing ", _jsx("strong", { children: filteredDiamonds.length }), " diamonds", canViewMockData && !canAccessRealInventory && (_jsx("span", { style: {
                                                            marginLeft: '12px',
                                                            fontSize: '11px',
                                                            background: 'var(--accent-gold, #996515)',
                                                            color: '#fff',
                                                            padding: '3px 10px',
                                                            fontWeight: 'bold',
                                                            textTransform: 'uppercase'
                                                        }, children: "Mock Data" }))] }), _jsxs(SortDropdown, { children: [_jsx("option", { children: "Sort by: Price (Low to High)" }), _jsx("option", { children: "Sort by: Price (High to Low)" }), _jsx("option", { children: "Sort by: Carat (Low to High)" }), _jsx("option", { children: "Sort by: Carat (High to Low)" })] })] })), !isLoading && !canAccessRealInventory && (_jsxs(RequestAccessBanner, { children: [_jsxs("h3", { children: [_jsx(FiSearch, { size: 20 }), "Live Inventory Access Required"] }), _jsx("p", { children: "You're viewing sample data. Request access to see our complete live inventory of 15,000+ certified diamonds with real-time availability and current pricing." }), _jsx(Button, { onClick: () => navigate('/contact'), children: "Request Live Inventory Access" })] })), !isLoading && totalPages > 1 && (_jsxs(MobileTopBar, { children: [_jsxs(MobilePaginationButton, { disabled: currentPage === 1, onClick: () => setCurrentPage(prev => prev - 1), children: [_jsx(FiChevronLeft, {}), " Prev"] }), _jsxs(MobilePageInfo, { children: ["Page ", currentPage, " of ", totalPages] }), _jsxs(MobilePaginationButton, { disabled: currentPage === totalPages, onClick: () => setCurrentPage(prev => prev + 1), children: ["Next ", _jsx(FiChevronRight, {})] })] })), isLoading && canAccessRealInventory && (_jsxs(_Fragment, { children: [_jsxs(LoadingHeader, { children: [_jsx(FiLoader, { size: 18 }), "Loading live diamond inventory..."] }), _jsx(GridContainer, { children: _jsx(DiamondsGridWrapper, { children: _jsx(DiamondsGrid, { children: Array.from({ length: 12 }).map((_, index) => (_jsxs(SkeletonCard, { children: [_jsx(SkeletonImage, {}), _jsxs(SkeletonContent, { children: [_jsx(SkeletonTitle, {}), _jsxs(SkeletonSpecs, { children: [_jsx(SkeletonSpec, {}), _jsx(SkeletonSpec, {}), _jsx(SkeletonSpec, {}), _jsx(SkeletonSpec, {})] }), _jsx(SkeletonText, {}), _jsx(SkeletonPrice, {}), _jsxs(SkeletonActions, { children: [_jsx(SkeletonButton, {}), _jsx(SkeletonButton, {})] })] })] }, index))) }) }) })] })), error && canAccessRealInventory && (_jsxs("div", { style: { textAlign: 'center', padding: '48px', color: '#ff6666', background: '#331111' }, children: [_jsx("h4", { children: "Failed to Load Live Inventory" }), _jsx("p", { children: error }), _jsx(Button, { onClick: loadInventory, style: { marginTop: '16px' }, children: "Retry" })] })), _jsxs(GridContainer, { children: [_jsxs(DiamondsGridWrapper, { id: "diamond-results-container", children: [!isLoading && !error && (_jsxs(_Fragment, { children: [_jsx(DiamondsGrid, { id: "tour-diamonds-grid", children: paginatedDiamonds.map((diamond, index) => {
                                                                    const normalizedDiamond = normalizeDiamond(diamond);
                                                                    const isFirstCard = index === 0;
                                                                    const hasCertificate = diamond.certificateNumber && (diamond.lab || diamond.certification);
                                                                    return (_jsxs(DiamondCardStyled, { id: isFirstCard ? 'tour-first-diamond' : undefined, className: hasCertificate && isFirstCard ? 'has-certificate-tour' : '', children: [_jsxs(CardImage, { children: [_jsx(LabBadge, { "$lab": diamond.lab || diamond.certification, children: diamond.lab || diamond.certification || 'CERT' }), _jsx(WatchlistButton, { "$active": watchlist.includes(normalizedDiamond.id), onClick: () => toggleWatchlist(normalizedDiamond.id), title: watchlist.includes(normalizedDiamond.id) ? "Remove from watchlist" : "Add to watchlist", children: _jsx(FiHeart, {}) }), _jsx("img", { src: getDiamondShapeImage(normalizedDiamond.shape), alt: `${diamond.shape} diamond` })] }), _jsxs(CardContent, { children: [_jsxs(CardTitle, { children: [diamond.shape, " Diamond"] }), _jsxs(CardSpecs, { id: isFirstCard ? 'tour-diamond-specs' : undefined, children: [_jsxs(SpecTag, { id: isFirstCard ? 'tour-diamond-carat' : undefined, children: [normalizedDiamond.carat, " ct"] }), _jsx(SpecTag, { id: isFirstCard ? 'tour-diamond-color' : undefined, children: normalizedDiamond.color }), _jsx(SpecTag, { id: isFirstCard ? 'tour-diamond-clarity' : undefined, children: normalizedDiamond.clarity }), _jsx(SpecTag, { id: isFirstCard ? 'tour-diamond-cut' : undefined, children: normalizedDiamond.cut || 'N/A' })] }), _jsxs(CardDetails, { children: [normalizedDiamond.cut || 'Cut Not Specified', " \u2022 ", diamond.fluorescence || 'No Fluor'] }), _jsx("div", { id: isFirstCard ? 'tour-diamond-price' : undefined, children: _jsx(ComponentGuard, { componentPermission: ComponentPermissions.VIEW_DIAMOND_PRICES, fallback: _jsx(CardPrice, { style: { color: 'var(--text-secondary)' }, children: "Contact for Pricing" }), children: _jsx(CardPrice, { children: normalizedDiamond.price }) }) }), _jsxs(CardActions, { children: [_jsx(ComponentGuard, { componentPermission: ComponentPermissions.VIEW_DIAMOND_PRICES, fallback: _jsx(CardButton, { disabled: true, "$primary": true, children: "View" }), children: _jsx(CardButton, { id: isFirstCard ? 'tour-view-details' : undefined, "$primary": true, onClick: () => {
                                                                                                        viewDetailsPermission.performAction(() => {
                                                                                                            setSelectedDiamond(normalizedDiamond);
                                                                                                            setDetailModalOpen(true);
                                                                                                            activityTracker.trackAction('viewed-diamond', `Viewed diamond ${normalizedDiamond.stockNumber || normalizedDiamond.id}`, {
                                                                                                                diamondId: normalizedDiamond.id,
                                                                                                                stockNumber: normalizedDiamond.stockNumber,
                                                                                                                carat: normalizedDiamond.carat,
                                                                                                                priceUSD: normalizedDiamond.priceUSD
                                                                                                            });
                                                                                                        });
                                                                                                    }, children: "View" }) }), _jsx(CardButton, { "$active": !!comparisonList.find(d => d.id === normalizedDiamond.id), onClick: () => {
                                                                                                    if (comparisonList.find(d => d.id === normalizedDiamond.id)) {
                                                                                                        removeFromComparison(normalizedDiamond.id);
                                                                                                        notify.info('Removed', 'Diamond removed from comparison');
                                                                                                    }
                                                                                                    else {
                                                                                                        addToComparison(normalizedDiamond);
                                                                                                    }
                                                                                                }, title: comparisonList.find(d => d.id === normalizedDiamond.id) ? "Remove from comparison" : "Add to comparison", children: comparisonList.find(d => d.id === normalizedDiamond.id) ? 'Added' : 'Compare' })] }), diamond.certificateNumber && (diamond.lab || diamond.certification) && getCertificateUrl(diamond.lab || diamond.certification || '', diamond.certificateNumber) && (_jsx(CardButtonFull, { onClick: () => {
                                                                                            const certUrl = getCertificateUrl(diamond.lab || diamond.certification || '', diamond.certificateNumber || '');
                                                                                            if (certUrl) {
                                                                                                window.open(certUrl, '_blank', 'noopener,noreferrer');
                                                                                            }
                                                                                        }, title: `View ${diamond.lab || diamond.certification} Certificate #${diamond.certificateNumber}`, id: isFirstCard ? 'tour-certificate-button' : undefined, children: "View Certificate" }))] })] }, diamond.id));
                                                                }) }), totalPages > 1 && (_jsxs(MobileBottomBar, { children: [_jsx(MobilePaginationButton, { disabled: currentPage === 1, onClick: () => setCurrentPage(1), children: "First" }), _jsx(MobilePaginationButton, { disabled: currentPage === 1, onClick: () => setCurrentPage(prev => prev - 1), children: "Prev" }), _jsxs("span", { style: {
                                                                            color: 'var(--text-primary)',
                                                                            fontWeight: '600',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            padding: '0 8px'
                                                                        }, children: [currentPage, "/", totalPages] }), _jsx(MobilePaginationButton, { disabled: currentPage === totalPages, onClick: () => setCurrentPage(prev => prev + 1), children: "Next" }), _jsx(MobilePaginationButton, { disabled: currentPage === totalPages, onClick: () => setCurrentPage(totalPages), children: "Last" })] }))] })), !isLoading && !error && filteredDiamonds.length === 0 && (_jsx(EmptyState, { children: "No diamonds match your current search criteria." }))] }), !isLoading && totalPages > 1 && (_jsxs(PaginationSidebar, { id: "tour-pagination-sidebar", children: [Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(page => (_jsx(PageButton, { id: page === 2 ? 'tour-page-number' : page === currentPage ? 'tour-current-page' : undefined, "$active": page === currentPage, onClick: () => setCurrentPage(page), children: page }, page))), totalPages > 10 && (_jsxs(_Fragment, { children: [_jsx("span", { style: { color: 'var(--text-tertiary)', fontSize: '11px', padding: '6px 0' }, children: "..." }), _jsx(PageButton, { "$active": currentPage === totalPages, onClick: () => setCurrentPage(totalPages), children: totalPages })] }))] }))] }), comparisonList.length > 0 && (_jsxs(FloatingCompareButton, { onClick: () => setComparisonModalOpen(true), children: [_jsx(FiLayers, {}), " Compare", _jsx(CompareCount, { children: comparisonList.length })] }))] })] })), activeTab === 'watchlist' && (_jsxs(FeatureCard, { "$hasAccess": !!canUseFeatures, children: [_jsxs(ResultsHeader, { children: [_jsxs(ResultsCount, { children: [_jsx(FiHeart, { style: { marginRight: '8px' } }), _jsx("strong", { children: watchlist.length }), " ", watchlist.length === 1 ? 'diamond' : 'diamonds', " saved"] }), watchlist.length > 0 && (_jsx(ClearButton, { onClick: () => {
                                            setWatchlist([]);
                                            localStorage.removeItem('diamond-watchlist');
                                            notify.info('Cleared', 'Watchlist has been cleared');
                                        }, children: "Clear All" }))] }), watchlist.length === 0 && (_jsxs(EmptyState, { children: [_jsx(FiHeart, { size: 32, style: { marginBottom: '16px', opacity: 0.5 } }), _jsx("p", { children: "Your watchlist is empty" }), _jsx("p", { style: { fontSize: '13px', marginTop: '8px' }, children: "Browse the inventory and click the heart icon to save diamonds" })] })), watchlist.length > 0 && (_jsx(GridContainer, { children: _jsx(DiamondsGridWrapper, { children: _jsx(DiamondsGrid, { children: diamonds
                                            .filter(d => watchlist.includes(d.id))
                                            .map(diamond => {
                                            const normalizedDiamond = normalizeDiamond(diamond);
                                            return (_jsxs(DiamondCardStyled, { children: [_jsxs(CardImage, { children: [_jsx(LabBadge, { "$lab": diamond.lab || diamond.certification, children: diamond.lab || diamond.certification || 'CERT' }), _jsx(WatchlistButton, { "$active": true, onClick: () => toggleWatchlist(normalizedDiamond.id), title: "Remove from watchlist", children: _jsx(FiHeart, {}) }), _jsx("img", { src: getDiamondShapeImage(normalizedDiamond.shape), alt: `${diamond.shape} diamond` })] }), _jsxs(CardContent, { children: [_jsxs(CardTitle, { children: [diamond.shape, " Diamond"] }), _jsxs(CardSpecs, { children: [_jsxs(SpecTag, { children: [normalizedDiamond.carat, " ct"] }), _jsx(SpecTag, { children: normalizedDiamond.color }), _jsx(SpecTag, { children: normalizedDiamond.clarity }), _jsx(SpecTag, { children: normalizedDiamond.cut || 'N/A' })] }), _jsxs(CardDetails, { children: [normalizedDiamond.cut || 'Cut Not Specified', " \u2022 ", diamond.fluorescence || 'No Fluor'] }), _jsx(ComponentGuard, { componentPermission: ComponentPermissions.VIEW_DIAMOND_PRICES, fallback: _jsx(CardPrice, { style: { color: 'var(--text-secondary)' }, children: "Price on request" }), children: _jsx(CardPrice, { children: normalizedDiamond.priceDisplay || `$${(normalizedDiamond.priceUSD || 0).toLocaleString()}` }) }), _jsxs(CardActions, { children: [_jsx(CardButton, { "$primary": true, onClick: () => {
                                                                            setSelectedDiamond(normalizedDiamond);
                                                                            setDetailModalOpen(true);
                                                                        }, children: "View" }), _jsx(CardButton, { onClick: () => toggleWatchlist(normalizedDiamond.id), children: "Remove" })] })] })] }, diamond.id));
                                        }) }) }) }))] })), _jsx(ComparisonModal, { diamonds: comparisonList, isOpen: comparisonModalOpen, onClose: () => setComparisonModalOpen(false), onRemove: removeFromComparison }), selectedDiamondForMemo && (_jsx(RequestMemoModal, { diamond: selectedDiamondForMemo, isOpen: memoModalOpen, onClose: () => {
                            setMemoModalOpen(false);
                            setSelectedDiamondForMemo(null);
                        }, onSuccess: () => {
                            navigate('/memos');
                        } })), selectedDiamond && detailModalOpen && (_jsx(DiamondDetailModal, { diamond: {
                            ...selectedDiamond,
                            caratWeight: selectedDiamond.caratWeight || parseFloat(selectedDiamond.carat || '1'),
                            polish: selectedDiamond.polish || 'Excellent',
                            symmetry: selectedDiamond.symmetry || 'Excellent',
                            fluorescence: selectedDiamond.fluorescence || 'None',
                            lab: selectedDiamond.lab || selectedDiamond.certification || 'GIA',
                            imageFilename: selectedDiamond.imageFilename || '',
                            status: selectedDiamond.status || 'available',
                            featured: selectedDiamond.featured || false,
                            certified: selectedDiamond.certified || true,
                            lastUpdated: selectedDiamond.lastUpdated || new Date().toISOString()
                        }, onClose: () => {
                            setDetailModalOpen(false);
                            setSelectedDiamond(null);
                        }, onRequestMemo: () => {
                            // Close the detail modal and open the memo request modal
                            setDetailModalOpen(false);
                            setSelectedDiamondForMemo(selectedDiamond);
                            setMemoModalOpen(true);
                        }, onCompare: (diamond) => {
                            // Add to comparison using the same handler as the main page
                            addToComparison(diamond);
                        }, onInquirePrice: () => {
                            setDetailModalOpen(false);
                            setPriceInquiryDiamond(selectedDiamond);
                            setPriceInquiryModalOpen(true);
                        } })), priceInquiryDiamond && (_jsx(PriceInquiryModal, { isOpen: priceInquiryModalOpen, onClose: () => {
                            setPriceInquiryModalOpen(false);
                            setPriceInquiryDiamond(null);
                        }, diamond: priceInquiryDiamond })), _jsx(BaseModal, { isOpen: exportModalOpen, onClose: () => setExportModalOpen(false), title: "Export Search Results", maxWidth: "500px", children: _jsxs("div", { style: { padding: '8px 0' }, children: [_jsxs(Text, { style: { marginBottom: '24px' }, children: ["Export ", filteredDiamonds.length, " filtered diamond", filteredDiamonds.length !== 1 ? 's' : '', " to your preferred format."] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px' }, children: [_jsx(Button, { variant: "primary", onClick: handleExportCSV, style: { width: '100%', padding: '16px', fontSize: '15px' }, children: "Export as CSV (Excel Compatible)" }), _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-secondary)', margin: '0', textAlign: 'center' }, children: "Best for Excel, Google Sheets, and data analysis" }), _jsx("div", { style: { height: '1px', background: 'var(--border-color)', margin: '12px 0' } }), _jsx(Button, { variant: "secondary", onClick: handleExportJSON, style: { width: '100%', padding: '16px', fontSize: '15px' }, children: "Export as JSON (with metadata)" }), _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-secondary)', margin: '0', textAlign: 'center' }, children: "Best for developers and system integrations" })] }), _jsx("div", { style: { marginTop: '24px', padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '4px', border: '1px solid var(--border-color)' }, children: _jsxs(Text, { style: { fontSize: '13px', color: 'var(--text-secondary)', margin: '0' }, children: [_jsx("strong", { children: "Exported data includes:" }), " Shape, Carat, Color, Clarity, Cut, Lab, Certificate #, Stock #, Price, Polish, Symmetry, Fluorescence, and all detailed specifications."] }) })] }) })] })] }));
};
export default Atelier;
